import { $isListItemNode } from "@lexical/list";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $addUpdateTag,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  defineExtension,
  HISTORY_PUSH_TAG,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  type LexicalExtension,
  type LexicalNode,
} from "lexical";
import type { BlockId } from "../../core/types";
import { BlockIdExtension } from "../block-id/extension";
import { $moveBlocksBy, $rootBlockOf } from "../block-id/operations";
import { $getBlockId } from "../block-id/state";
import { $selectedBlocks, BlockSelectionExtension } from "../block-selection/extension";

/** The item that exists only to hold a nested list is not something a user moves by itself. */
const $isOwnItem = (node: LexicalNode): boolean =>
  $isListItemNode(node) && node.getFirstChild()?.getType() !== "list";

/**
 * What Alt+Shift+arrow moves: the selected blocks, or else the list item the caret is in, or else
 * the root-level block it is in (the table, from inside a cell). A list item moves within its
 * list; a root block among root blocks.
 */
export function $reorderTarget(): BlockId[] {
  const blocks = $selectedBlocks();
  const nodes: (LexicalNode | null)[] = blocks ?? [];
  if (blocks === null) {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return [];
    const anchor = selection.anchor.getNode();
    nodes.push($findMatchingParent(anchor, $isOwnItem) ?? $rootBlockOf(anchor));
  }
  return nodes
    .map((node) => (node === null ? null : $getBlockId(node)))
    .filter((id): id is BlockId => id !== null);
}

/**
 * Reordering without a pointer. Alt+Shift+Up and Alt+Shift+Down are what Google Docs uses for the
 * same thing. The moved nodes keep their keys, so the caret or the block selection travels along.
 */
export const BlockReorderExtension: LexicalExtension<
  Record<never, never>,
  "z-editor/block-reorder",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/block-reorder",
  dependencies: [BlockIdExtension, BlockSelectionExtension],
  register(editor) {
    const move =
      (direction: "up" | "down") =>
      (event: KeyboardEvent | null): boolean => {
        if (event === null || !event.altKey || !event.shiftKey) return false;
        if (event.metaKey || event.ctrlKey || !editor.isEditable()) return false;
        const ids = $reorderTarget();
        if (ids.length === 0) return false;
        event.preventDefault();
        const outcome = $moveBlocksBy(ids, direction);
        if (outcome.ok && outcome.moved) $addUpdateTag(HISTORY_PUSH_TAG);
        return true;
      };
    return mergeRegister(
      editor.registerCommand(KEY_ARROW_UP_COMMAND, move("up"), COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_ARROW_DOWN_COMMAND, move("down"), COMMAND_PRIORITY_HIGH),
    );
  },
});
